import { apiRequest, HttpMethod } from '@hqo/shared-modules/dist/api-request';
import { QueryKey, useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { GetUtilityButtonsAPIResponse, UtilityButton } from 'app/data/types/utility-button';
import { useApi } from 'app/shared/contexts/api';
import { DeviceType, isDesktopHost } from 'app/shared/consts';
import { useSdkHostInfo } from 'app/shared/contexts/host-info';
import { useOpenParams } from 'app/shared/hooks/use-open-params/use-open-params.hook';

export const UTILITY_BUTTONS_QUERY_KEY = 'utility-buttons';

export const getUseUtilityButtonsQueryKey = (
  buildingUuid: string,
  locale: string,
  deviceType: DeviceType,
): QueryKey => [UTILITY_BUTTONS_QUERY_KEY, buildingUuid, locale, deviceType];

/**
 * The building's utility buttons — what the tenant portal draws its home cards from.
 *
 * The feed uses them for two things: the icon a building chose for a vertical, and whether that vertical
 * exists here at all. Deliberately NOT surfaced as an error: an icon falling back to the default and a
 * create button staying hidden are both quiet degradations, and a failed lookup should not put an error
 * banner over a feed that loaded fine.
 */
export const useUtilityButtonsQuery = ({ enabled = true }: { enabled?: boolean } = {}): {
  utilityButtons: UtilityButton[];
  isLoading: boolean;
} => {
  const { apiUrl, getAuthHeaders, getDefaultHeaders } = useApi();
  const { buildingUuid } = useOpenParams();
  const { locale } = useIntl();
  const { hostPlatform, isHostInfoSettled } = useSdkHostInfo();
  const deviceType = isDesktopHost(hostPlatform) ? DeviceType.WEB : DeviceType.MOBILE;

  const getUtilityButtons = async (): Promise<UtilityButton[]> => {
    const headers = await getAuthHeaders();
    const response = await apiRequest<GetUtilityButtonsAPIResponse, void, void>({
      // `VERTICAL_ADAPTER` is what makes the endpoint resolve `feature_type` for buttons that carry no
      // `vertical` of their own — without it a work-order button cannot be placed.
      url: `${apiUrl}/api/tenant/v1/buildings/${buildingUuid}/utility-buttons?lang=${encodeURIComponent(locale)}&include=VERTICAL_ADAPTER&deviceType=${deviceType}`,
      method: HttpMethod.GET,
      headers: { ...headers, ...getDefaultHeaders() },
    });

    if (response && !response.isEmpty()) {
      return response.data.data.utility_buttons ?? [];
    }

    return [];
  };

  const { data, isLoading } = useQuery<UtilityButton[], Error>({
    queryKey: getUseUtilityButtonsQueryKey(buildingUuid, locale, deviceType),
    queryFn: getUtilityButtons,
    // `enabled` lets a caller that only wants these buttons on ONE host avoid fetching them on the
    // others. The feed always wants them (icons), so it defaults on.
    enabled: enabled && isHostInfoSettled && !!buildingUuid && !!apiUrl,
    retry: false,
  });

  return { utilityButtons: data ?? [], isLoading };
};
